import React, { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toEpochMs } from '@/lib/schedule-datetime';
import {
  DateFormWidget,
  Error,
  FormButtons,
  FormRow,
  TagsInput,
} from '../controls';
import {
  ModalEntityHelp,
  PERFORMER_ADD_HELP_STEPS,
} from '../modal/ModalEntityHelp';

export const ADD_NEW_PERFORMER_VALUE = '__new__';

const emptyNewPerformer = {
  performerId: 0,
  performerName: '',
  hometown: '',
  type: '',
  subtype: '',
  performerDescription: '',
  links: {
    instagram: '',
    twitter: '',
    facebook: '',
    web: '',
  },
};

function buildDefaultValues(performance) {
  const defaultDateObject = {
    startTime: performance.startTime || null,
    endTime: performance.endTime || null,
    startTimeMS: toEpochMs(performance.startTimeMS, performance.startTime),
    endTimeMS: toEpochMs(performance.endTimeMS, performance.endTime),
    selectedDate: performance.selectedDate ?? null,
    dateInput: performance.dateInput ?? null,
  };

  const selected = performance.performer || {};
  const hasExisting =
    selected.performerId != null &&
    selected.performerId !== '' &&
    selected.performerId !== 0;

  return {
    performanceNotes: performance.performanceNotes || '',
    performer: hasExisting ? selected : {},
    performerId: hasExisting ? selected.performerId : '',
    dates: [defaultDateObject],
  };
}

/**
 * @param {object} props
 * @param {object} props.performance
 * @param {(vals: object) => void} props.handleSave
 * @param {(dates: object[]) => void} props.handleDateChange
 * @param {object[]} props.performers
 * @param {{ types?: string[], subtypes?: string[] }} [props.performerTypes]
 * @param {(performer: object) => void} props.performerUpdated
 * @param {boolean} [props.showHelp]
 * @param {(show: boolean) => void} [props.onShowHelp]
 */
export function PerformanceForm({
  performance,
  handleSave,
  handleDateChange,
  performers,
  performerTypes = {},
  performerUpdated,
  showHelp = false,
  onShowHelp,
}) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: buildDefaultValues(performance),
    mode: 'onSubmit',
  });

  const performanceId = performance?.performanceId;
  const performerIdValue = useWatch({ control, name: 'performerId' });
  const isCreatingNew = performerIdValue === ADD_NEW_PERFORMER_VALUE;

  useEffect(() => {
    reset(buildDefaultValues(performance));
  }, [performanceId, reset]);

  const dates = buildDefaultValues(performance).dates;
  const types = performerTypes.types || [];
  const subtypes = performerTypes.subtypes || [];

  const onSubmit = (vals) => {
    if (vals.performerId === ADD_NEW_PERFORMER_VALUE) {
      const newPerformer = vals.performer || {};
      handleSave({
        ...vals,
        performer: {
          ...newPerformer,
          performerId: 0,
          type: newPerformer.type || '',
          subtype: newPerformer.subtype || '',
        },
        performerId: 0,
      });
      return;
    }
    handleSave(vals);
  };

  if (showHelp) {
    return (
      <ModalEntityHelp
        entityNoun="performer"
        steps={PERFORMER_ADD_HELP_STEPS}
        showHelp
        onShowHelp={onShowHelp}
        onBack={() => onShowHelp?.(false)}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalEntityHelp
        entityNoun="performer"
        steps={PERFORMER_ADD_HELP_STEPS}
        showHelp={false}
        onShowHelp={() => onShowHelp?.(true)}
        onBack={() => onShowHelp?.(false)}
      />
      <FormRow label="Performer">
        <Controller
          name="performerId"
          control={control}
          rules={{
            validate: (value) => (value ? true : 'Performer is required'),
          }}
          render={({ field }) => (
            <select
              className="h-10 w-full rounded-[20px] border border-[var(--input)] px-4 outline-none"
              value={field.value === '' ? '' : String(field.value)}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) {
                  field.onChange('');
                  setValue('performer', {});
                  performerUpdated?.({});
                  return;
                }
                if (id === ADD_NEW_PERFORMER_VALUE) {
                  field.onChange(ADD_NEW_PERFORMER_VALUE);
                  setValue('performer', { ...emptyNewPerformer });
                  performerUpdated?.({ ...emptyNewPerformer });
                  return;
                }
                const selected = performers.find(
                  (p) => String(p.performerId) === id,
                );
                field.onChange(selected?.performerId ?? '');
                setValue('performer', selected || {});
                if (selected) {
                  performerUpdated(selected);
                }
              }}
            >
              <option value="">Select a performer</option>
              {performers.map((performer) => (
                <option
                  key={performer.performerId}
                  value={String(performer.performerId)}
                >
                  {performer.performerName}
                </option>
              ))}
            </select>
          )}
        />
        {errors.performerId ? (
          <Error>{errors.performerId.message}</Error>
        ) : null}
      </FormRow>

      {isCreatingNew ? (
        <>
          <FormRow
            label="Performer Name"
            error={errors.performer?.performerName?.message}
          >
            <input
              type="text"
              placeholder="Name"
              {...register('performer.performerName', {
                required: 'Performer name is required',
              })}
            />
          </FormRow>
          <FormRow label="Hometown">
            <input
              type="text"
              placeholder="Hometown"
              {...register('performer.hometown')}
            />
          </FormRow>
          <FormRow label="Type" error={errors.performer?.type?.message}>
            <Controller
              name="performer.type"
              control={control}
              rules={{ required: 'Type is required' }}
              render={({ field }) => (
                <TagsInput
                  value={field.value}
                  placeholder="Select or enter a type"
                  options={types}
                  onChange={field.onChange}
                />
              )}
            />
          </FormRow>
          <FormRow label="Subtype">
            <Controller
              name="performer.subtype"
              control={control}
              render={({ field }) => (
                <TagsInput
                  value={field.value}
                  placeholder="Select or enter a subtype"
                  options={subtypes}
                  onChange={field.onChange}
                />
              )}
            />
          </FormRow>
          <FormRow label="Biography">
            <textarea
              placeholder="Performer Biography..."
              {...register('performer.performerDescription')}
            />
          </FormRow>
          <FormRow label="Website">
            <input
              type="text"
              placeholder="Website"
              {...register('performer.links.web')}
            />
          </FormRow>
        </>
      ) : null}

      <DateFormWidget
        label="Date & Times"
        dates={dates}
        handleDateChange={handleDateChange}
        required
      />
      <FormRow label="Notes">
        <textarea
          placeholder="Performance Notes..."
          {...register('performanceNotes')}
        />
      </FormRow>
      <FormButtons title="Performance" />
    </form>
  );
}
